import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import Footer from "components/Footer";

import AllProjects from './pages/all-projects';
import CreateProjectFlow from './pages/create-project-flow';
import ProjectComparison from './pages/project-comparison';
import VendorQuoteRequestLanding from './pages/vendor-quote-request-landing';
import VendorProjectDetails from './pages/vendor-project-details';
import ProjectMilestoneTracker from './pages/project-milestone-tracker';
import BuyerVendorMessagingInterface from './pages/buyer-vendor-messaging-interface';
import VendorHubDashboard from './pages/vendor-hub-dashboard';
import VendorAnalyticsAdvertising from './pages/vendor-analytics-advertising';
import VendorAnalyticsDashboard from './pages/vendor-analytics-dashboard';
import CheckoutProcess from './pages/checkout-process';
import PaymentConfirmation from './pages/payment-confirmation';
import UnifiedNotificationCenter from './pages/unified-notification-center';
import HowItWorks from './pages/how-it-works';
import VendorHowItWorks from './pages/vendor-how-it-works';
import HowItWorksSelector from './pages/how-it-works-selector';
import LoginSignUp from './pages/login-sign-up';
import ComingSoonLanding from './pages/coming-soon-landing';
import TermsAndConditions from './pages/terms-and-conditions';
import PrivacyPolicy from './pages/privacy-policy';
import CookiePolicy from './pages/cookie-policy';
import FinancialDisclosure from './pages/financial-disclosure';
import AffiliateDisclosure from './pages/affiliate-disclosure';
import AboutUs from './pages/about';
import AuthConfirm from './pages/auth-confirm';
import UnsubscribePage from './pages/unsubscribe';
import UserSettings from './pages/user-settings';
import FlightSearch from './pages/flight-search';
import { useAuth } from './contexts/AuthContext';

import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';

// Demo vendor emails allowed to bypass coming-soon and access vendor workflows
const DEMO_VENDOR_EMAILS = [
  'melondropp95@gmail.com',
  'michael@abgmultifamily.com',
  'michael@atlasprocurementsolution.us',
  'mcrocefoglia95@gmail.com',
];

const isDemoVendor = (user) => {
  if (!user?.email) return false;
  return DEMO_VENDOR_EMAILS?.includes(user?.email?.toLowerCase());
};

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  // Not logged in → coming soon
  if (!user) {
    return <Navigate to="/coming-soon-landing" replace />;
  }

  // Logged in but not a demo account → coming soon
  if (!isDemoVendor(user)) {
    return <Navigate to="/coming-soon-landing" replace />;
  }

  return children;
};

// Route accessible only to demo vendor accounts (bypasses coming-soon lock)
const DemoVendorRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/coming-soon-landing" replace />;
  }

  if (!isDemoVendor(user)) {
    return <Navigate to="/coming-soon-landing" replace />;
  }

  return children;
};

const RoutesInner = () => {
  useGoogleAnalytics();
  return null;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <RoutesInner />
      <RouterRoutes>
        <Route path="/login-sign-up" element={<LoginSignUp />} />
        <Route path="/auth/confirm" element={<AuthConfirm />} />
        <Route path="/" element={<Navigate to="/coming-soon-landing" replace />} />
        <Route path="/all-projects" element={<ProtectedRoute><AllProjects /></ProtectedRoute>} />
        <Route path="/create-project-flow" element={<ProtectedRoute><CreateProjectFlow /></ProtectedRoute>} />
        <Route path="/project-comparison" element={<ProtectedRoute><ProjectComparison /></ProtectedRoute>} />
        <Route path="/vendor-quote-request-landing" element={<VendorQuoteRequestLanding />} />
        <Route path="/vendor-project-details" element={<DemoVendorRoute><VendorProjectDetails /></DemoVendorRoute>} />
        <Route path="/vendor-project-details/:projectId" element={<DemoVendorRoute><VendorProjectDetails /></DemoVendorRoute>} />
        <Route path="/project-milestone-tracker" element={<DemoVendorRoute><ProjectMilestoneTracker /></DemoVendorRoute>} />
        <Route path="/buyer-vendor-messaging-interface" element={<DemoVendorRoute><BuyerVendorMessagingInterface /></DemoVendorRoute>} />
        <Route path="/vendor-hub-dashboard" element={<DemoVendorRoute><VendorHubDashboard /></DemoVendorRoute>} />
        <Route path="/vendor-analytics-advertising" element={<DemoVendorRoute><VendorAnalyticsAdvertising /></DemoVendorRoute>} />
        <Route path="/vendor-analytics-dashboard" element={<DemoVendorRoute><VendorAnalyticsDashboard /></DemoVendorRoute>} />
        <Route path="/checkout-process" element={<ProtectedRoute><CheckoutProcess /></ProtectedRoute>} />
        <Route path="/payment-confirmation" element={<ProtectedRoute><PaymentConfirmation /></ProtectedRoute>} />
        <Route path="/unified-notification-center" element={<ProtectedRoute><UnifiedNotificationCenter /></ProtectedRoute>} />
        <Route path="/how-it-works" element={<HowItWorks />} />
        <Route path="/vendor-how-it-works" element={<VendorHowItWorks />} />
        <Route path="/how-it-works-selector" element={<HowItWorksSelector />} />
        <Route path="/coming-soon-landing" element={<ComingSoonLanding />} />
        <Route path="/unsubscribe" element={<UnsubscribePage />} />
        <Route path="/user-settings" element={<ProtectedRoute><UserSettings /></ProtectedRoute>} />
        <Route path="/flight-search" element={<ProtectedRoute><FlightSearch /></ProtectedRoute>} />
        <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/financial-disclosure" element={<FinancialDisclosure />} />
        <Route path="/affiliate-disclosure" element={<AffiliateDisclosure />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="*" element={<Navigate to="/coming-soon-landing" replace />} />
      </RouterRoutes>
      <Footer />
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;